import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import { authService } from '@/api';
import type { LC_ErrorOut ,
  LC_AuthenticateIn,
  LC_PasswordRecoveryIn,
  LC_PasswordRecoveryOut,
  RexSystem,
  User,
  AuthState,
} from '@/api';
import { storeAuth, clearStoredAuth } from '@/utils/storage';
import { isSSOSession } from '@/utils/sso';
import { extractTokenInfo, mapLoginResponseToUser, mapRexSystemsFromResponse } from '@/api/domains/auth/auth.mapper';
import { getRexSSOLoginUrl } from '@/api/core/apiConfig';
import { addNotification } from '@/store/slices/uiSlice';


type AuthSliceState = AuthState;

const initialState: AuthSliceState = {
  user: null,
  token: null,
  tokenExpiry: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  isSSO: false,
  isInitializing: true,
  accountLinkRequired: false,
  rexSystems: [],
};

export const loginAsync = createAsyncThunk<
  { user: User; token: string; tokenExpiry: string; accountLinkRequired: boolean;
    rexSystems: RexSystem[] },
  LC_AuthenticateIn,
  { rejectValue: LC_ErrorOut }
>(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials);
      if (response.success && response.data) {
        const user = authService.mapLoginResponseToUser(response.data);
        const { token, tokenExpiry } = extractTokenInfo(response.data);
        const isSSO = isSSOSession(token);
        const rexSystems = mapRexSystemsFromResponse(response.data.rexSystems);
        storeAuth({ user, token, tokenExpiry, isSSO, rexSystems });
        const accountLinkRequired = response.data.accountLinkRequired ?? false;
        return { user, token, tokenExpiry, accountLinkRequired, rexSystems };
      }
      return rejectWithValue(response.error || { description: 'Error de autenticación' } as any);
    } catch (error) {
      return rejectWithValue({ description: error instanceof Error ? error.message : 'Error de conexión' } as any);
    }
  },
);

export const recoverPasswordAsync = createAsyncThunk<
  LC_PasswordRecoveryOut,
  LC_PasswordRecoveryIn,
  { rejectValue: LC_ErrorOut }
>(
  'auth/recoverPassword',
  async (data, { rejectWithValue }) => {
    try {
      const response = await authService.recoverPassword(data);
      if (response.success && response.data) {
        return response.data;
      }
      return rejectWithValue(response.error || { description: 'Error al recuperar contraseña' } as any);
    } catch (error) {
      return rejectWithValue({ description: error instanceof Error ? error.message : 'Error de conexión' } as any);
    }
  },
);

type CheckPendingSSOResult = {
  user: User; token: string; tokenExpiry: string; isSSO: boolean;
  accountLinkRequired: boolean; rexSystems: RexSystem[];
};

export const checkPendingSSO = createAsyncThunk<
  CheckPendingSSOResult | null,
  void,
  { state: { auth: AuthSliceState } }
>(
  'auth/checkPendingSSO',
  async (_, { getState, dispatch }) => {
    const { token, tokenExpiry } = getState().auth;
    if (token && tokenExpiry && !authService.isTokenExpired(new Date(tokenExpiry))) {
      return null;
    }

    const response = await authService.checkPendingSSO();

    if (response.status === 204) return null;

    if (response.success && response.data) {
      const user = mapLoginResponseToUser(response.data);
      const { token: newToken, tokenExpiry: newExpiry } = extractTokenInfo(response.data);
      const isSSO = isSSOSession(newToken);
      const rexSystems = mapRexSystemsFromResponse(response.data.rexSystems);
      storeAuth({ user, token: newToken, tokenExpiry: newExpiry, isSSO, rexSystems });
      const accountLinkRequired = response.data.accountLinkRequired ?? false;
      return {
        user, token: newToken, tokenExpiry: newExpiry, isSSO, accountLinkRequired, rexSystems,
      };
    }

    if (!response.success && response.error) {
      const code = response.error.code;
      // Token Rex+ expirado → redirigir al inicio de sesión de Rex+
      if (code === 'SSO_TOKEN_INVALID') {
        window.location.href = getRexSSOLoginUrl();
        return null;
      }
      // Usuario/empresa con problema → mostrar notificación y caer al login normal
      if (
        code === 'SSO_USER_INACTIVE' ||
        code === 'SSO_COMPANY_INACTIVE' ||
        code === 'SSO_USER_NOT_FOUND'
      ) {
        const message = response.error.detail || response.error.description || response.error.code || '';
        dispatch(addNotification({ type: 'error', message, duration: 8000 }));
        return null;
      }
    }

    // 502, 503 u otros: degradación silenciosa → login normal
    return null;
  },
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: () => {
      clearStoredAuth();
      return { ...initialState, isInitializing: false };
    },
    clearError: (state) => {
      state.error = null;
    },
    clearAccountLinkRequired: (state) => {
      state.accountLinkRequired = false;
    },
    initComplete: (state) => {
      state.isInitializing = false;
    },
    initializeFromStorage: (state, action: PayloadAction<{
      user: User;
      token: string;
      tokenExpiry: string;
      rexSystems?: RexSystem[];
    }>) => {
      const { user, token, tokenExpiry, rexSystems } = action.payload;
      const expiry = new Date(tokenExpiry);
      if (!authService.isTokenExpired(expiry)) {
        state.user = user;
        state.token = token;
        state.tokenExpiry = tokenExpiry;
        state.isAuthenticated = true;
        state.isInitializing = false;
        state.rexSystems = rexSystems ?? [];
      } else {
        clearStoredAuth();
        return { ...initialState, isInitializing: false };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.tokenExpiry = action.payload.tokenExpiry;
        state.isAuthenticated = true;
        state.isSSO = isSSOSession(action.payload.token);
        state.accountLinkRequired = action.payload.accountLinkRequired;
        state.rexSystems = action.payload.rexSystems;
        state.error = null;
      })
      .addCase(loginAsync.rejected, (state, action) => {
        state.loading = false;
        const err = action.payload;
        state.error = (err as any)?.description || 'Error de autenticación';
      })
      // Recover Password (no cambia auth state principal)
      .addCase(recoverPasswordAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(recoverPasswordAsync.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(recoverPasswordAsync.rejected, (state, action) => {
        state.loading = false;
        const err = action.payload;
        state.error = (err as any)?.description || 'Error al recuperar contraseña';
      })
      // Check Pending SSO
      .addCase(checkPendingSSO.pending, (state) => {
        state.isInitializing = true;
      })
      .addCase(checkPendingSSO.fulfilled, (state, action) => {
        state.isInitializing = false;
        if (action.payload) {
          state.user = action.payload.user;
          state.token = action.payload.token;
          state.tokenExpiry = action.payload.tokenExpiry;
          state.isAuthenticated = true;
          state.isSSO = action.payload.isSSO;
          state.accountLinkRequired = action.payload.accountLinkRequired;
          state.rexSystems = action.payload.rexSystems;
        }
      })
      .addCase(checkPendingSSO.rejected, (state) => {
        state.isInitializing = false;
      });
  },
});

export const {
  logout, clearError, clearAccountLinkRequired, initComplete, initializeFromStorage,
} = authSlice.actions;
export default authSlice.reducer;
